/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {UserSummaryModel} from '../../../api/ms-security/contracts/user-summary.model';
import {SelectInTableColorModel} from '../../../shared/select-in-table/models/select-in-table-color.model';
import {UserManagementService} from '../../../api/ms-security/user-management/user-management.service';
import {LoadingService} from '../../../shared/loading/loading-service/loading.service';
import {
    Observable,
    of,
    ReplaySubject,
    Subscription
} from 'rxjs';
import {Router} from '@angular/router';
import {UserTableModel} from './models/user-table.model';
import {UserResultModel} from '../../../api/ms-security/contracts/user-result.model';
import {SortDirection} from '../../../api/generic/contracts/sort-direction.enum';
import {UserService} from '../../../shared/services/user.service';
import {Permission} from '../../../shared/utils/permission.enum';
import {DropdownType} from '../../../shared/select-in-table/models/dropdown-type.enum';
import {ReferenceDataService} from '../../../api/ms-security/reference-data/reference-data.service';
import {PartnerBaseModel} from '../../../api/ms-security/contracts/partner-base.model';
import {PartnersGroupedByCountryModel} from './models/partners-grouped-by-country.model';
import {DropdownOptionModel} from '../../../shared/select-in-table/models/dropdown-option.model';
import {UserDisplayPipe} from '../../../shared/pipes/user-display/user-display.pipe';
import {UserTableFilterColumnEnum} from './models/user-table-filter-column.enum';
import {CodeFilterModel} from '../../../shared/filter/code-filter/code-filter.model';
import {ToastWrapperService} from '../../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {WithPagination} from '../../../shared/pagination/with-pagination';
import {SecurityService} from '../../../api/ms-security/security.service';
import {GroupTypeEnum} from '../../../api/ms-security/contracts/group-type.enum';
import {EntityGroupDetailContract} from '../../../api/ms-security/contracts/entity-group/entity-group-detail.contract';
import {EntityGroupDetailMapper} from './models/mappers/entity-group-detail.mapper';
import {SpinnerService} from '../../../shared/spinner/spinner.service';
import {SpinnerContextTypeEnum} from '../../../shared/spinner/spinner-context-type.enum';
import {
    finalize,
    skip
} from 'rxjs/operators';
import {UserSearchCriteriaService} from '../services/user-search-criteria.service';
import {CodeFilterComponent} from '../../../shared/filter/code-filter/code-filter.component';
import {InputFilterComponent} from '../../../shared/filter/input-filter/input-filter.component';
import {AbstractFilterStateModel} from '../../../shared/filter/abstract-filter-state.model';
import {StatefulComponent} from '../../../shared/stateful/stateful.component';
import {UsersTableStateModel} from './models/users-table-state.model';
import {ChipSelectorZoneDropdownComponent} from '../../../shared/chip-selector/option-components/chip-selector-zone-dropdown/chip-selector-zone-dropdown.component';
import {EntityGroupDetailDropdownOptionModel} from './models/entity-group-detail-dropdown-option.model';
import {UserEntityGroupDetailContract} from '../../../api/ms-security/contracts/user-entity-group-detail.contract';
import {UserRoleBaseContract} from '../../../api/ms-security/contracts/user-role-base.contract';
import {BaseChipSelectorZoneInputModel} from '../../../shared/chip-selector/base-chip-selector-zone-input.model';
import {BaseChipSelectorZoneOutputModel} from '../../../shared/chip-selector/base-chip-selector-zone-output.model';
import {CountryDropdownOptionModel} from './models/country-dropdown-option.model';
import {EntityGroupDetailSortByEnum} from './models/mappers/entity-group-detail-sort-by.enum';
import {PartnersGroupedByCountryMapper} from './models/mappers/partners-grouped-by-country.mapper';
import {RoleDetailContract} from '../../../api/ms-security/contracts/role-detail.contract';
import {UserContextEnum} from '../../../api/ms-security/contracts/user-context.enum';
import {EntityGroupTypeDetailContract} from '../../../api/ms-security/contracts/entity-group/entity-group-type-detail.contract';
import {ChipSelectorZoneDropdownInputModel} from '../../../shared/chip-selector/option-components/chip-selector-zone-dropdown/chip-selector-zone-dropdown-input.model';

@Component({
    selector: 'app-users-table',
    templateUrl: './users-table.component.html',
    styleUrls: ['./users-table.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UsersTableComponent extends StatefulComponent<UsersTableStateModel> implements OnInit, OnDestroy, WithPagination {
    /** Input to receive the new users to add to the table **/
    @Input() addUserToTable: Observable<UserSummaryModel>;
    /** Input with the user context 'Partner' or ASCC **/
    @Input() context: UserContextEnum;
    @ViewChild('technicianGroups', {static: true}) tooltipTechnicianTemplateRef: TemplateRef<any>;
    @ViewChild('userFilterComponent') userFilterComponent: InputFilterComponent;
    @ViewChild('roleFilterComponent') roleFilterComponent: CodeFilterComponent;
    @ViewChild('partnerComponent') partnerFilterComponent: CodeFilterComponent;
    @ViewChild('technicianGroupComponent') technicianGroupFilterComponent: CodeFilterComponent;
    @ViewChild('supplierComponent') supplierFilterComponent: CodeFilterComponent;
    @ViewChild('supplyingPackingPlantComponent') supplyingPackingPlantFilterComponent: CodeFilterComponent;
    @ViewChild('customerComponent') customerFilterComponent: CodeFilterComponent;
    /** Set initial roles select component values**/
    public rolesDropdownOptions: DropdownOptionModel[] = [];
    public partnerGroupedByCountryOptions: CountryDropdownOptionModel[];
    public entityGroupTechnicianOptions: EntityGroupDetailDropdownOptionModel[];
    public entityGroupsSupplyingPackingPlantOptions: EntityGroupDetailDropdownOptionModel[];
    public entityGroupsSupplierOptions: EntityGroupDetailDropdownOptionModel[];
    public entityGroupsCustomerOptions: EntityGroupDetailDropdownOptionModel[];
    public readonly rolesColor: SelectInTableColorModel = {
        text: '#04a594',
        border: '#04a594',
        secondaryText: '#92a2bd'
    };
    /** Set initial Membership table values**/
    public usersList: UserTableModel[] = [];
    public allAvailableRoles: UserRoleBaseContract[] = [];
    public allAvailablePartners: PartnerBaseModel[] = [];
    public dropdownType = DropdownType;
    public UserTableFilterColumn = UserTableFilterColumnEnum;
    public partnerFilterOptions: CodeFilterModel[];
    public roleFilterOptions: CodeFilterModel[];
    public techniciansFilterOptions: CodeFilterModel[];
    public supplyingPackingPlantsFilterOptions: CodeFilterModel[];
    public suppliersFilterOptions: CodeFilterModel[];
    public customersFilterOptions: CodeFilterModel[];
    /** Event emitter for successful submission **/
    public submittedWithSuccess = new EventEmitter<boolean>();
    /** Subscriptions variable */
    readonly GroupTypeEnum = GroupTypeEnum;
    private subscriptions: Subscription = new Subscription();
    /** Local loading spinner */
    private readonly LOCAL_LOADING_INSTANCE_KEY = 'USERS_TABLE_LIST_LOADING';
    public spinnerObservable = this.spinnerService.onLocalChange(this.LOCAL_LOADING_INSTANCE_KEY);
    private readonly TOOLTIP_DISABLE_USER_ROLE =
            'You can\'t apply this role because this user has already a role that can\'t coexist with this one.';
    private readonly SUPPLIER_FEATURE_TOGGLE_KEY = 'feature:toggle.settlements.supplier';
    private onReady = new ReplaySubject<boolean>(1);

    constructor(private rolesService: UserManagementService,
                private toast: ToastWrapperService,
                private loadingService: LoadingService,
                private router: Router,
                private userPermissionsService: UserService,
                private securityService: SecurityService,
                private referenceDataService: ReferenceDataService,
                private userDisplayPipe: UserDisplayPipe,
                private spinnerService: SpinnerService,
                public userSearchCriteriaService: UserSearchCriteriaService) {
        super();
        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, this.constructor);
        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, null);
    }

    /** Sets the visibility of the clear all filters button **/
    get showClearAllFilters(): boolean {
        return this.userFilterComponent?.isFilterActive
                || this.roleFilterComponent?.isFilterActive
                || (this.state.showPartnerColumn && this.partnerFilterComponent?.isFilterActive)
                || this.supplierFilterComponent?.isFilterActive
                || (this.state.showTechnicianGroupsColumn && this.technicianGroupFilterComponent?.isFilterActive)
                || (this.state.showSupplyingPackingPlantsColumn && this.supplyingPackingPlantFilterComponent?.isFilterActive)
                || (this.state.showCustomerColumn && this.customerFilterComponent?.isFilterActive);
    }

    get entityGroupsPartnerOptions(): EntityGroupDetailDropdownOptionModel[] {
        return this.partnerGroupedByCountryOptions?.reduce((sum, value) => [...sum, ...value.children],
                <EntityGroupDetailDropdownOptionModel[]>[])
                .filter((value) => value.value.country.active !== false);
    }

    ngOnInit(): void {
        /* Clear search filters on init page*/
        this.userSearchCriteriaService.clear();

        /** Get the users permissions**/
        this.setUserPermissions();

        /** Subscription to add new users to the table **/
        this.addUsers();

        /** Get all available roles from the BE and populate Roles filter options **/
        this.getAllAvailableRolesAndFilterOptions();

        this.storeState({
            hasAdminPermission: this.userPermissionsService.hasPermissions([
                Permission.ADMIN_ROLE_GRANT,
                Permission.ADMIN_ROLE_REVOKE,
                Permission.ADMIN_ROLE_VIEW,
                Permission.PARTNER_ROLE_GRANT,
                Permission.PARTNER_ROLE_REVOKE,
                Permission.PARTNER_ROLE_VIEW
            ], 'any')
        });

        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, this.ngOnInit);
        this.subscriptions.add(
                this.referenceDataService.getEntityContextDetail(this.context)
                        .subscribe((showEntityGroupTypeColumns) => {

                            this.onReady.pipe(skip(showEntityGroupTypeColumns.length - 1))
                                    .subscribe(() => {
                                        this.getUsersWithRoles();
                                    });

                            if (this.showEntityGroupTypeColumn(showEntityGroupTypeColumns, GroupTypeEnum.PARTNER)) {
                                this.getPartners();
                            }
                            if (this.showEntityGroupTypeColumn(showEntityGroupTypeColumns, GroupTypeEnum.TECHNICIAN)) {
                                this.getTechnicians();
                            }
                            if (this.showEntityGroupTypeColumn(showEntityGroupTypeColumns, GroupTypeEnum.SUPPLIER)) {
                                this.getSuppliers();
                            }
                            if (this.showEntityGroupTypeColumn(showEntityGroupTypeColumns, GroupTypeEnum.PLANNER)) {
                                this.getSupplyingPackingPlants();
                            }
                            if (this.showEntityGroupTypeColumn(showEntityGroupTypeColumns, GroupTypeEnum.CUSTOMER)) {
                                this.getCustomers();
                            }

                        })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    showEntityGroupTypeColumn(entityGroupTypeDetail: EntityGroupTypeDetailContract[], columnType: GroupTypeEnum): boolean {
        if (entityGroupTypeDetail != null && columnType != null) {
            return entityGroupTypeDetail.filter((item) => item.code === columnType).length > 0;
        }
    }

    partnerZones(value: EntityGroupDetailContract, user: UserTableModel): BaseChipSelectorZoneInputModel[] {
        return [
            {
                type: ChipSelectorZoneDropdownComponent,
                input: {
                    canEdit: false,
                    placeholder: 'Select a partner',
                    options: this.getUserPartnerDropdownOptions(user),
                    initialValue: this.partnerGroupedByCountryOptions
                            ?.find((country) => country.value?.numericCode === value?.country.numericCode)
                            ?.children
                            ?.find((partner) => partner.value.code === value?.code)
                }
            }
        ];
    }

    supplyingPackingPlantZones(value: EntityGroupDetailContract,
                               user: UserTableModel): BaseChipSelectorZoneInputModel[] {
        return [
            {
                type: ChipSelectorZoneDropdownComponent,
                input: {
                    canEdit: false,
                    placeholder: 'Select a supplying packing plant',
                    options: this.getUserSupplyingPackingPlantDropdownOptions(user),
                    initialValue: this.entityGroupsSupplyingPackingPlantOptions?.find((item) => item.value?.code === value?.code)
                }
            }
        ];
    }

    technicianGroupsZones(value: EntityGroupDetailContract,
                          user: UserTableModel): BaseChipSelectorZoneInputModel[] {
        return [
            {
                type: ChipSelectorZoneDropdownComponent,
                input: {
                    canEdit: false,
                    placeholder: 'Select a technician group',
                    options: this.getUserTechnicianGroupsDropdownOptions(user),
                    initialValue: this.entityGroupTechnicianOptions?.find((item) => item.value?.code === value?.code)
                }
            }
        ];
    }

    supplierZones(value: EntityGroupDetailContract,
                  user: UserTableModel): (BaseChipSelectorZoneInputModel | ChipSelectorZoneDropdownInputModel)[] {
        const options = this.getUserSupplierDropdownOptions(user);
        return [
            {
                type: ChipSelectorZoneDropdownComponent,
                input: {
                    hasUserInput: true,
                    canEdit: false,
                    placeholder: 'Select a supplier',
                    options,
                    initialValue: this.entityGroupsSupplierOptions?.find((item) => item.value?.code === value?.code),
                    searchFunction: (term): Observable<EntityGroupDetailDropdownOptionModel[]> => {
                        let finalOptions = options;

                        if (term?.length > 0) {
                            finalOptions = options.filter((option) => `${option.value.code}${option.value.name}`.toLowerCase()
                                    .indexOf(term.toLowerCase()) > -1);
                        }

                        return of(finalOptions);
                    },
                    inputFormatter: (option: EntityGroupDetailDropdownOptionModel): string =>
                            `${option?.value?.code} - ${option?.value?.name}`
                }
            }
        ];
    }

    customerZones(value: EntityGroupDetailContract,
                  user: UserTableModel): (BaseChipSelectorZoneInputModel | ChipSelectorZoneDropdownInputModel)[] {
        return [
            {
                type: ChipSelectorZoneDropdownComponent,
                input: {
                    canEdit: false,
                    placeholder: 'Select a customer',
                    options: this.getUserCustomerDropdownOptions(user),
                    initialValue: this.entityGroupsCustomerOptions?.find((item) => item.value?.code === value?.code)
                }
            }
        ];
    }

    /**
     * Retrieves users with roles from the BE
     **/
    getUsersWithRoles = (): void => {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        const query = this.userSearchCriteriaService.getUserQuery(this.context);
        this.subscriptions.add(
                this.rolesService.getUsersWithRoles(query)
                        .pipe(finalize(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.constructor);
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                        }))
                        .subscribe((usersResult) => {
                            this.usersList = this.mapUsers(usersResult);
                            if (this.usersList?.length === 0 && this.userSearchCriteriaService.pageNumber > 1) {
                                this.changePage(this.userSearchCriteriaService.pageNumber - 1);
                            }
                        })
        );
    };

    /**
     * Sets the user permissions
     */
    setUserPermissions(): void {
        this.storeState({
            canGrantRole: this.userPermissionsService
                    .hasPermissions([Permission.ADMIN_ROLE_GRANT, Permission.PARTNER_ROLE_GRANT], 'any', this.context),
            canAddPartner: this.userPermissionsService
                    .hasPermissions([Permission.ADMIN_ROLE_GRANT, Permission.PARTNER_ROLE_GRANT], 'any', this.context),
            canAddSupplyingPackingPlant: this.userPermissionsService
                    .hasPermissions([Permission.ADMIN_ROLE_GRANT], 'any', this.context),
            canAddSupplier: this.userPermissionsService
                    .hasPermissions([Permission.ADMIN_ROLE_GRANT], 'any', this.context),
            canAddCustomer: this.userPermissionsService
                    .hasPermissions([Permission.ADMIN_ROLE_GRANT], 'any', this.context),
            canAddTechnicianGroup: this.userPermissionsService
                    .hasPermissions([Permission.ADMIN_ROLE_GRANT], 'any', this.context),
            canRevokeRole: this.userPermissionsService
                    .hasPermissions([Permission.ADMIN_ROLE_REVOKE, Permission.PARTNER_ROLE_REVOKE], 'any', this.context),
            canRemovePartner: this.userPermissionsService
                    .hasPermissions([Permission.ADMIN_ROLE_REVOKE, Permission.PARTNER_ROLE_REVOKE], 'any', this.context)
        });
    }

    /**
     * Creates subscription to add user to the table
     */
    addUsers(): void {
        this.subscriptions.add(
                this.addUserToTable.subscribe((newUser) => {
                    if (this.usersList) {
                        this.usersList.unshift({
                            user: newUser,
                            canAddRoles: true,
                            canAddPartners: true,
                            canAddTechnicianGroups: true,
                            canAddSupplyingPackingPlants: true,
                            canAddSuppliers: true,
                            canAddCustomers: true
                        });
                    }
                })
        );
    }

    /**
     * Retrieves all the existing roles
     **/
    getAllAvailableRolesAndFilterOptions = (): void => {
        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, this.getAllAvailableRolesAndFilterOptions);
        this.subscriptions.add(
                this.referenceDataService.getRoles(this.context)
                        .pipe(finalize(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.getAllAvailableRolesAndFilterOptions);
                        }))
                        .subscribe((value) => {
                            this.allAvailableRoles = value.map((role) => ({
                                role: {
                                    code: role.code,
                                    description: role.description,
                                    maxNumberOfDays: role.maxNumberOfDays,
                                    expirationWarningDays: role.expirationWarningDays,
                                    excludedRoles: role.excludedRoles
                                }, expiresOn: null
                            }));

                            this.roleFilterOptions = value.map((role) => ({
                                name: role.description,
                                code: role.code,
                                checked: true
                            } as CodeFilterModel));
                        }, (error) => {
                            this.router.navigate(['/', 'spo']);
                        }));
    };

    /**
     * Maps {@link UserResultModel} to an {@link UserTableModel} array
     * @param result The {@link UserResultModel} to map
     **/
    mapUsers = (result: UserResultModel): UserTableModel[] => {
        this.userSearchCriteriaService.totalResults = result.totalResults;

        if (result.usersPage == null) {
            return [];
        }
        return result.usersPage.map((tableUser): UserTableModel => {
            this.getUserDropdownOptions(tableUser);

            return {
                user: tableUser,
                canAddRoles: (this.rolesDropdownOptions?.length ?? 0) > 0,
                canAddPartners: this.userCanAddPartner(tableUser.groups),
                canAddTechnicianGroups: this.userCanAddTechnicianGroups(tableUser.groups),
                canAddSupplyingPackingPlants: this.userCanAddSupplyingPackingPlant(tableUser.groups),
                canAddSuppliers: this.userCanAddSupplier(tableUser.groups),
                canAddCustomers: this.userCanAddCustomer(tableUser.groups)
            };
        });
    };

    /**
     * Creates a new role component in the table row
     * @param tableUser The user were the component should be created
     * **/
    addNewRole = (tableUser: UserTableModel): void => {
        const tableUserRoles = tableUser.user.roles;
        if (tableUserRoles.length > 0 && tableUserRoles[tableUserRoles.length - 1].role.code === null) {
            return null;
        } else {
            const user = tableUser.user;
            const newRole: UserRoleBaseContract = {
                role: {code: null, description: null, maxNumberOfDays: null},
                expiresOn: null
            };
            user.roles.push(newRole);
            this.getUserDropdownOptions(user);
        }
    };

    /**
     * Add a new entity group element component in the table row
     * @param tableUser The user were the component should be created
     * @param groupType The group type were the component should be added
     */
    addNewElementToEntityGroup(tableUser: UserTableModel, groupType: GroupTypeEnum): void {
        this.removeNullFromEntityGroup(tableUser, groupType);

        let group = tableUser.user.groups.find((userGroup) => userGroup.groupType === groupType);

        if (group == null) {
            group = {
                groupType,
                entityGroups: []
            };

            tableUser.user.groups.push(group);
        }
        group.entityGroups.push(null);
    }

    /**
     * Created a filtered array for the user roles dropdown options
     * @param user The user which the dropdown options should be filtered
     **/
    getUserDropdownOptions = (user: UserSummaryModel): void => {
        const userRoles = user.roles.map((role) => role.role.code);
        const allRoles = this.allAvailableRoles.map((role) => role.role.code);

        const newRoles = allRoles.filter((role) => !userRoles.includes(role));

        const rolesForDropdown = this.allAvailableRoles.filter((role) => newRoles.includes(role.role.code));

        if (rolesForDropdown) {
            this.rolesDropdownOptions = rolesForDropdown.map((currentRole) => ({
                code: currentRole.role.code,
                description: currentRole.role.description,
                maxNumberOfDays: currentRole.role.maxNumberOfDays,
                expirationWarningDays: currentRole.role.expirationWarningDays
            }));

            this.userExcludedRoles(user.roles, this.allAvailableRoles);

        }
    };

    userExcludedRoles(userRoles: UserRoleBaseContract[], allRoles: UserRoleBaseContract[]): void {
        userRoles.forEach((userRole) => {
            (allRoles.find((role) => role.role.code === userRole.role.code)
                    ?.role as RoleDetailContract)?.excludedRoles?.forEach((role) => {
                this.rolesDropdownOptions.forEach((item) => {
                    if (item.code === role.code) {
                        item.disabled = true;
                        item.tooltip = this.TOOLTIP_DISABLE_USER_ROLE;
                    }
                });
            });
        });
    }

    /**
     * Revokes a role from an tableUser and the select component from the UI if a valid role code is emitted from the select component
     * Removes select component from the UI if no valid code role is selected
     * Sets the add button user flag
     * @param tableUser The user in the{@link usersList} array
     * @param role The role in the {@link UserSummaryModel.roles} array
     * **/
    revokeRole = (tableUser: UserTableModel, role: UserRoleBaseContract): void => {
        const saveRole = role.role.code;

        if (role.role.code && this.context != null) {
            /** Starts table loading **/
            this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
            this.subscriptions.add(
                    this.rolesService.revokeRole(tableUser.user.username, role.role.code, this.context)
                            .pipe(finalize(() => {
                                this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                            }))
                            .subscribe(() => {
                                tableUser.user.roles = tableUser.user.roles.filter((currentRole) => currentRole !== role);
                                this.getUserDropdownOptions(tableUser.user);
                                tableUser.canAddRoles = this.rolesDropdownOptions.length > 0;
                                this.removeUserFromTable(tableUser);

                                this.triggerRevokeSuccessToast('Role', saveRole, this.userDisplayPipe.transform(tableUser.user, true));

                            }, (error) => {
                                this.triggerRevokeErrorToast('role');
                            }));
        } else {
            tableUser.user.roles = tableUser.user.roles.filter((currentRole) => currentRole.role.code !== role.role.code);
        }
    };

    revokeEntityGroup = (tableUser: UserTableModel, entityElement: EntityGroupDetailContract, groupType: GroupTypeEnum): void => {
        if (entityElement == null) {
            this.removeFromEntityGroup(tableUser, entityElement, groupType);
        } else {
            const entityToastLabel = this.entityGroupTypeToastLabel(groupType);

            if (this.context != null) {
                this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);

                this.subscriptions.add(
                        this.rolesService.revokeEntityGroup(tableUser.user.username, groupType, entityElement.code, this.context)
                                .pipe(finalize(() => {
                                    this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                                }))
                                .subscribe(() => {
                                    this.removeFromEntityGroup(tableUser, entityElement, groupType);
                                    tableUser.canAddTechnicianGroups = this.userCanAddTechnicianGroups(tableUser.user.groups);
                                    tableUser.canAddSupplyingPackingPlants = this.userCanAddSupplyingPackingPlant(tableUser.user.groups);
                                    tableUser.canAddPartners = this.userCanAddPartner(tableUser.user.groups);
                                    tableUser.canAddSuppliers = this.userCanAddSupplier(tableUser.user.groups);
                                    tableUser.canAddCustomers = this.userCanAddCustomer(tableUser.user.groups);
                                    this.removeUserFromTable(tableUser);

                                    this.triggerRevokeSuccessToast(entityToastLabel, entityElement.name,
                                            this.userDisplayPipe.transform(tableUser.user, true));

                                }, (error) => {
                                    this.triggerRevokeErrorToast(entityToastLabel?.toLowerCase());
                                }));
            }
        }
    };

    /**
     * Grants a new role to an tableUser
     * If the last dropdown option was selected, disables add button for that user
     * @param selectedRole The role to be granted to the tableUser
     * @param tableUser The tableUser index in the {@link usersList} array
     * **/
    grantRole = (tableUser: UserTableModel, selectedRole: DropdownOptionModel): void => {
        const userRoles = tableUser.user.roles;
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        const isRoleUpdate = userRoles.filter((role) => role.role.code === selectedRole.code).length === 1;
        let successToastMessage: string;
        /** Check if is a role update **/
        if (isRoleUpdate) {
            const roleToUpdate = userRoles.filter((role) => role.role.code === selectedRole.code)[0];
            /** If the date hasn't changed, return **/
            if (roleToUpdate.expiresOn === selectedRole.date) {
                this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.grantRole);
                return;
            }
            /** Set toast message for role update **/
            successToastMessage = `The <strong>expiration date has been changed</strong> for Role ${selectedRole.code}`;
        } else {
            /** Set toast message for new role **/
            successToastMessage = `Role <strong>${selectedRole.code}</strong> added to user ` +
                    `<strong>${this.userDisplayPipe.transform(tableUser.user, true)}</strong> successfully`;
        }

        if (this.context != null) {
            this.subscriptions.add(
                    this.rolesService.grantRole(tableUser.user.username, selectedRole.code, selectedRole.date, this.context)
                            .pipe(finalize(() => {
                                this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                            }))
                            .subscribe(() => {
                                /** If isn't a role update, add the new role to the user row **/
                                if (!isRoleUpdate) {
                                    const newRole = this.allAvailableRoles.find((r) => r.role.code === selectedRole.code);
                                    userRoles[userRoles.length - 1].role = newRole.role;
                                    userRoles[userRoles.length - 1].expiresOn = selectedRole.date;
                                }
                                if (this.rolesDropdownOptions.length === 1) {
                                    tableUser.canAddRoles = false;
                                }
                                this.toast.success(successToastMessage, 'Success');
                                this.submittedWithSuccess.emit(true);
                            }, () => {
                                this.triggerGrantErrorToast('role');
                                this.submittedWithSuccess.emit(false);
                                userRoles.pop();
                            }));
        }
    };

    grantEntityGroup = (tableUser: UserTableModel, entityElement: EntityGroupDetailContract, groupType: GroupTypeEnum): void => {
        const entityGroups = this.removeNullFromEntityGroup(tableUser, groupType);
        const entityLabel = this.entityGroupTypeToastLabel(groupType);

        if (entityElement?.code != null) {

            const userGroups = tableUser.user.groups.find((userGroup) => userGroup.groupType === groupType)?.entityGroups;
            const hasEntity = userGroups.find((item) => item.code === entityElement.code) != null;
            if (!hasEntity && this.context != null) {
                this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                this.subscriptions.add(
                        this.rolesService.grantEntityGroup(tableUser.user.username, groupType, entityElement.code, this.context)
                                .pipe(finalize(() => {
                                    tableUser.canAddTechnicianGroups = this.userCanAddTechnicianGroups(tableUser.user.groups);
                                    tableUser.canAddSupplyingPackingPlants = this.userCanAddSupplyingPackingPlant(tableUser.user.groups);
                                    tableUser.canAddPartners = this.userCanAddPartner(tableUser.user.groups);
                                    tableUser.canAddSuppliers = this.userCanAddSupplier(tableUser.user.groups);
                                    tableUser.canAddCustomers = this.userCanAddCustomer(tableUser.user.groups);
                                    this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                                }))
                                .subscribe(() => {
                                    entityGroups.push({...entityElement});

                                    this.triggerGrantSuccessToast(entityLabel, entityElement.name,
                                            this.userDisplayPipe.transform(tableUser.user, true));

                                }, () => {
                                    this.removeFromEntityGroup(tableUser, entityElement, groupType);
                                    this.triggerGrantErrorToast(entityLabel?.toLowerCase());
                                }));
            }
        }
    };

    /** Sets all filters as inactive **/
    setAllFiltersInactive(): void {
        this.userFilterComponent?.resetFilter();
        this.roleFilterComponent?.resetFilter();
        if (this.state.showPartnerColumn) {
            this.partnerFilterComponent?.resetFilter();
        }
        if (this.state.showTechnicianGroupsColumn) {
            this.technicianGroupFilterComponent?.resetFilter();
        }
        if (this.state.showSupplyingPackingPlantsColumn) {
            this.supplyingPackingPlantFilterComponent?.resetFilter();
        }
        if (this.state.showSupplierColumn) {
            this.supplierFilterComponent?.resetFilter();
        }
        if (this.state.showCustomerColumn) {
            this.customerFilterComponent?.resetFilter();
        }
    }

    /** Clears all filters **/
    clearAllFilters(): void {
        this.userSearchCriteriaService.clear();
        this.setAllFiltersInactive();
        this.storeState({isNameArrowUp: true});
        this.getUsersWithRoles();
    }

    /**
     * Changes the User list page and gets the corresponding Users
     * @param pageNumber The page that the list will change to
     */
    changePage(pageNumber: number): void {
        this.userSearchCriteriaService.pageNumber = pageNumber;
        this.getUsersWithRoles();
    }

    hasPagination(): boolean {
        return this.userSearchCriteriaService.totalResults > this.userSearchCriteriaService.pageSize;
    }

    /**
     * Toggles ordering arrows
     */
    arrowToggle(): void {
        this.storeState({
            isNameArrowUp: (this.userSearchCriteriaService.sortDirection === SortDirection.ASC
                    && this.userSearchCriteriaService.sortedBy === UserTableFilterColumnEnum.IDENTIFICATION)
        });
    }

    /**
     * Set material number filter *
     * {@link changePage} keeps the selected page number
     * @param state The emitted code filter state
     */
    onFilterChange(state: AbstractFilterStateModel): void {
        this.userSearchCriteriaService.setState(state.filterName as UserTableFilterColumnEnum, state, () => {
            this.changePage(this.userSearchCriteriaService.pageNumber);
        });
    }

    /**
     * Determines by which criteria the User list should be ordered
     * @param columnSelected The ordering criteria
     * @param arrowUp If the corresponding arrow orientation is up
     */
    sortingBy(columnSelected: UserTableFilterColumnEnum, arrowUp: boolean): void {
        if (this.userSearchCriteriaService.sortedBy === columnSelected && !arrowUp) {
            this.userSearchCriteriaService.sortDirection = SortDirection.ASC;
        } else {
            this.userSearchCriteriaService.sortDirection = SortDirection.DESC;
        }
        this.userSearchCriteriaService.sortedBy = columnSelected;
        this.getUsersWithRoles();
    }

    getUserTechnicianGroups(tableUser: UserTableModel): EntityGroupDetailContract[] {
        return tableUser?.user?.groups?.find((group) => group.groupType === GroupTypeEnum.TECHNICIAN)
                ?.entityGroups;
    }

    getUserSupplyingPackingPlants(tableUser: UserTableModel): EntityGroupDetailContract[] {
        return tableUser?.user?.groups?.find((group) => group.groupType === GroupTypeEnum.PLANNER)
                ?.entityGroups;
    }

    getUserSuppliers(tableUser: UserTableModel): EntityGroupDetailContract[] {
        return tableUser?.user?.groups?.find((group) => group.groupType === GroupTypeEnum.SUPPLIER)
                ?.entityGroups;
    }

    getUserCustomers(tableUser: UserTableModel): EntityGroupDetailContract[] {
        return tableUser?.user?.groups?.find((group) => group.groupType === GroupTypeEnum.CUSTOMER)
                ?.entityGroups;
    }

    getUserPartners(tableUser: UserTableModel): EntityGroupDetailContract[] {
        return tableUser?.user?.groups?.find((group) => group.groupType === GroupTypeEnum.PARTNER)
                ?.entityGroups;
    }

    deleteEntityGroup(user: UserTableModel,
                      zones: BaseChipSelectorZoneOutputModel<EntityGroupDetailDropdownOptionModel>[],
                      groupType: GroupTypeEnum): void {
        if (zones[0].isEdit === false || zones[0].isEdit == null) {
            this.revokeEntityGroup(user, zones[0]?.value?.value, groupType);
        } else {
            this.removeFromEntityGroup(user, null, groupType);
        }
    }

    closeEntityGroup(user: UserTableModel,
                     zones: BaseChipSelectorZoneOutputModel<EntityGroupDetailDropdownOptionModel>[],
                     groupType: GroupTypeEnum): void {
        this.grantEntityGroup(user, zones[0]?.value?.value, groupType);
    }

    triggerRevokeSuccessToast(entityLabel: string, entityName: string, userLabel: string): void {
        this.toast.success(
                `${entityLabel} <strong>${entityName}</strong> revoked from user <strong>${userLabel}</strong> successfully`,
                'Success');
    }

    triggerGrantSuccessToast(entityLabel: string, entityName: string, userLabel: string): void {
        this.toast.success(
                `${entityLabel} <strong>${entityName}</strong> added to user <strong>${userLabel}</strong> successfully`,
                'Success');
    }

    triggerGrantErrorToast(entityLabel: string): void {
        this.toast.error(`An error was occurred. Unable to grant ${entityLabel}`, 'Error');
    }

    triggerRevokeErrorToast(entityLabel: string): void {
        this.toast.error(`An error was occurred. Unable to revoke ${entityLabel}`, 'Error');
    }

    removeUserFromTable(tableUser: UserTableModel): void {
        if (tableUser.user.roles?.length === 0
                && (tableUser.user.groups?.find((group) => group.groupType === GroupTypeEnum.PARTNER)
                        ?.entityGroups?.length ?? 0) === 0
                && (tableUser.user.groups?.find((group) => group.groupType === GroupTypeEnum.TECHNICIAN)
                        ?.entityGroups?.length ?? 0) === 0
                && (tableUser.user.groups?.find((group) => group.groupType === GroupTypeEnum.PLANNER)
                        ?.entityGroups?.length ?? 0) === 0
                && (tableUser.user.groups?.find((group) => group.groupType === GroupTypeEnum.SUPPLIER)
                        ?.entityGroups?.length ?? 0) === 0
                && (tableUser.user.groups?.find((group) => group.groupType === GroupTypeEnum.CUSTOMER)
                        ?.entityGroups?.length ?? 0) === 0
        ) {
            this.usersList = this.usersList.filter((currentUser) => currentUser !== tableUser);
        }
    }

    entityGroupTypeToastLabel(groupType: GroupTypeEnum): string {
        let label;
        switch (groupType) {
            case GroupTypeEnum.PLANNER:
                label = 'Supplying packing plant';
                break;
            case GroupTypeEnum.TECHNICIAN:
                label = 'Technician group';
                break;
            case GroupTypeEnum.SUPPLIER:
                label = 'Supplier';
                break;
            case GroupTypeEnum.PARTNER:
                label = 'Partner';
                break;
            case GroupTypeEnum.CUSTOMER:
                label = 'Customer';
                break;
        }
        return label;
    }

    groupPartnersByCountry(partners: EntityGroupDetailContract[]):
            PartnersGroupedByCountryModel[] {
        const userPartnerGroups: PartnersGroupedByCountryModel[] = [];
        const userCountriesCodes: string[] = [];
        for (const partner of partners) {
            if (!userCountriesCodes.includes(partner.country.numericCode)) {
                userCountriesCodes.push(partner.country.numericCode);

                userPartnerGroups.push({
                    country: partner.country,
                    partners: [
                        {
                            code: partner.code,
                            active: partner.active,
                            name: partner.name
                        }
                    ]
                });
            } else {
                userPartnerGroups.find((group) => group.country.numericCode === partner.country.numericCode)
                        .partners
                        .push({
                            code: partner.code,
                            active: partner.active,
                            name: partner.name
                        });
            }
        }
        return userPartnerGroups;
    }

    removeFromEntityGroup(user: UserTableModel, element: DropdownOptionModel, type: GroupTypeEnum): EntityGroupDetailContract[] {
        const entityGroups = user.user.groups.find((userGroup) => userGroup.groupType === type)?.entityGroups;
        const entityGroupsToRemove = entityGroups?.filter((group) => group?.code === element?.code);

        entityGroupsToRemove?.forEach((group) => entityGroups?.splice(entityGroups?.findIndex((item) => item?.code === group?.code), 1));
        return entityGroups;
    }

    removeNullFromEntityGroup(user: UserTableModel, type: GroupTypeEnum): EntityGroupDetailContract[] {
        return this.removeFromEntityGroup(user, null, type);
    }

    private userCanAddPartner(userGroups: UserEntityGroupDetailContract[]): boolean {
        return this.userCanAddToEntityGroup(userGroups, this.entityGroupsPartnerOptions, GroupTypeEnum.PARTNER);
    }

    private userCanAddSupplyingPackingPlant(userGroups: UserEntityGroupDetailContract[]): boolean {
        return this.userCanAddToEntityGroup(userGroups, this.entityGroupsSupplyingPackingPlantOptions, GroupTypeEnum.PLANNER);
    }

    private userCanAddTechnicianGroups(userGroups: UserEntityGroupDetailContract[]): boolean {
        return this.userCanAddToEntityGroup(userGroups, this.entityGroupTechnicianOptions, GroupTypeEnum.TECHNICIAN);
    }

    private userCanAddSupplier(userGroups: UserEntityGroupDetailContract[]): boolean {
        return this.userCanAddToEntityGroup(userGroups, this.entityGroupsSupplierOptions, GroupTypeEnum.SUPPLIER);
    }

    private userCanAddCustomer(userGroups: UserEntityGroupDetailContract[]): boolean {
        return this.userCanAddToEntityGroup(userGroups, this.entityGroupsCustomerOptions, GroupTypeEnum.CUSTOMER);
    }

    private userCanAddToEntityGroup(userGroups: UserEntityGroupDetailContract[],
                                    allDropdownOptions: EntityGroupDetailDropdownOptionModel[],
                                    groupType: GroupTypeEnum): boolean {
        return (allDropdownOptions?.length ?? 0) > (userGroups
                ?.find((group) => group.groupType === groupType)
                ?.entityGroups?.length ?? 0);
    }

    private getUserPartnerDropdownOptions(user: UserTableModel): CountryDropdownOptionModel[] {
        let options: CountryDropdownOptionModel[] = [];

        if (user != null && this.partnerGroupedByCountryOptions != null) {
            const userPartners = user.user?.groups?.find((group) => group.groupType === GroupTypeEnum.PARTNER)?.entityGroups;

            options = this.partnerGroupedByCountryOptions.map((country) => ({
                ...country,
                children: country.children
                        .filter((partner) => partner.value.active &&
                                !userPartners?.find((userPartner) => userPartner?.code === partner.value?.code))
            }))
                    .filter((country) => country.children.length > 0 && country.value.active);
        }

        return options;
    }

    private getUserSupplyingPackingPlantDropdownOptions(user: UserTableModel): EntityGroupDetailDropdownOptionModel[] {
        let options: EntityGroupDetailDropdownOptionModel[] = [];
        if (user != null && this.entityGroupsSupplyingPackingPlantOptions != null) {
            const userSpps = user.user?.groups?.find((userGroup) => userGroup.groupType === GroupTypeEnum.PLANNER)?.entityGroups;
            options = this.entityGroupsSupplyingPackingPlantOptions
                    .filter((option) => !userSpps?.find((item) => item?.code === option?.value?.code));
        }
        return options;
    }

    private getUserTechnicianGroupsDropdownOptions(user: UserTableModel): EntityGroupDetailDropdownOptionModel[] {
        let options: EntityGroupDetailDropdownOptionModel[] = [];
        if (user != null && this.entityGroupTechnicianOptions != null) {
            const userTechnician = user.user?.groups?.find((userGroup) => userGroup.groupType === GroupTypeEnum.TECHNICIAN)?.entityGroups;
            options = this.entityGroupTechnicianOptions
                    .filter((option) => !userTechnician?.find((item) => item?.code === option?.value?.code));
        }
        return options;
    }

    private getUserSupplierDropdownOptions(user: UserTableModel): EntityGroupDetailDropdownOptionModel[] {
        let options: EntityGroupDetailDropdownOptionModel[] = [];
        if (user != null && this.entityGroupsSupplierOptions != null) {
            const userSupplier = user.user?.groups?.find((userGroup) => userGroup.groupType === GroupTypeEnum.SUPPLIER)?.entityGroups;
            options = this.entityGroupsSupplierOptions
                    .filter((option) => !userSupplier?.find((item) => item?.code === option?.value?.code));
        }
        return options;
    }

    private getUserCustomerDropdownOptions(user: UserTableModel): EntityGroupDetailDropdownOptionModel[] {
        let options: EntityGroupDetailDropdownOptionModel[] = [];
        if (user != null && this.entityGroupsCustomerOptions != null) {
            const userCustomer = user.user?.groups?.find((userGroup) => userGroup.groupType === GroupTypeEnum.CUSTOMER)?.entityGroups;
            options = this.entityGroupsCustomerOptions
                    .filter((option) => !userCustomer?.find((item) => item?.code === option?.value?.code));
        }
        return options;
    }

    private getPartners(): void {
        if (this.state.hasAdminPermission) {
            this.referenceDataService.getEntityGroupTypeDetail(GroupTypeEnum.PARTNER, this.context)
                    .pipe(finalize(() => {
                        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.ngOnInit);
                        this.onReady.next(true);
                    }))
                    .subscribe((partners) => {
                        this.partnerGroupedByCountryOptions = PartnersGroupedByCountryMapper
                                .toCountryDropdownOptionModelArray(this.groupPartnersByCountry(partners));
                        this.partnerFilterOptions = PartnersGroupedByCountryMapper.toCodeFilterModelArray(
                                this.groupPartnersByCountry(partners));
                        this.storeState({showPartnerColumn: true});
                    });
        }
    }

    private getTechnicians(): void {
        if (this.state.hasAdminPermission) {
            this.referenceDataService.getEntityGroupTypeDetail(GroupTypeEnum.TECHNICIAN, this.context)
                    .pipe(finalize(() => {
                        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.ngOnInit);
                        this.onReady.next(true);
                    }))
                    .subscribe((technicianGroups) => {
                        this.techniciansFilterOptions = EntityGroupDetailMapper
                                .toCodeFilterModelArray(technicianGroups, EntityGroupDetailSortByEnum.NAME);
                        this.entityGroupTechnicianOptions = EntityGroupDetailMapper
                                .toTechnicianGroupDropdownOptionModelArray(technicianGroups, this.tooltipTechnicianTemplateRef);
                        this.storeState({showTechnicianGroupsColumn: true});
                    });
        }
    }

    private getSuppliers(): void {
        if (this.state.hasAdminPermission) {
            this.referenceDataService.getEntityGroupTypeDetail(GroupTypeEnum.SUPPLIER, this.context)
                    .pipe(finalize(() => {
                        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.ngOnInit);
                        this.onReady.next(true);
                    }))
                    .subscribe((suppliers) => {
                        this.suppliersFilterOptions = EntityGroupDetailMapper.toCodeFilterModelArray(suppliers);
                        this.entityGroupsSupplierOptions = EntityGroupDetailMapper.toSupplierDropdownOptionModelArray(suppliers);
                        this.storeState({showSupplierColumn: true});
                    });
        }
    }

    private getCustomers(): void {
        if (this.state.hasAdminPermission) {
            this.referenceDataService.getEntityGroupTypeDetail(GroupTypeEnum.CUSTOMER, this.context)
                    .pipe(finalize(() => {
                        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.ngOnInit);
                        this.onReady.next(true);
                    }))
                    .subscribe((customers) => {
                        this.customersFilterOptions = EntityGroupDetailMapper.toCodeFilterModelArray(customers);
                        this.entityGroupsCustomerOptions = EntityGroupDetailMapper.toSupplierDropdownOptionModelArray(customers);
                        this.storeState({showCustomerColumn: true});
                    });
        }
    }

    private getSupplyingPackingPlants(): void {
        if (this.state.hasAdminPermission) {
            this.referenceDataService.getEntityGroupTypeDetail(GroupTypeEnum.PLANNER, this.context)
                    .pipe(finalize(() => {
                        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.ngOnInit);
                        this.onReady.next(true);
                    }))
                    .subscribe((supplyingPackingPlants) => {
                        this.supplyingPackingPlantsFilterOptions = EntityGroupDetailMapper
                                .toCodeFilterModelArray(supplyingPackingPlants);
                        this.entityGroupsSupplyingPackingPlantOptions = EntityGroupDetailMapper
                                .toSupplyingPackingPlantDropdownOptionModelArray(supplyingPackingPlants);
                        this.storeState({showSupplyingPackingPlantsColumn: true});
                    });
        }
    }
}
